/**
 * View History utilities.
 *
 * Track and display recently viewed listings.
 * Client-side only — stored in localStorage.
 * No server component needed.
 *
 * Stores: [{ listingId, title, imageUrl, price, viewedAt }] (max 20)
 */

export const STORAGE_KEY = 'ith:viewHistory';
export const MAX_HISTORY = 20;

/**
 * Add a listing to view history.
 * @param {{ listingId, title, imageUrl, price }} listing
 */
export const addToHistory = (listing) => {
  if (typeof localStorage === 'undefined') return;
  if (!listing?.listingId) return;

  const history = getHistory();
  const filtered = history.filter(l => l.listingId !== listing.listingId);
  const updated = [{ ...listing, viewedAt: new Date().toISOString() }, ...filtered].slice(0, MAX_HISTORY);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch (_) {}
};

/**
 * Get view history from localStorage.
 * @returns {Object[]}
 */
export const getHistory = () => {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
};

/**
 * Remove a listing from history.
 * @param {string} listingId
 */
export const removeFromHistory = (listingId) => {
  if (typeof localStorage === 'undefined') return;
  const history = getHistory().filter(l => l.listingId !== listingId);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(history)); } catch (_) {}
};

/**
 * Clear all view history.
 */
export const clearHistory = () => {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
};

/**
 * Check if a listing was recently viewed.
 * @param {string} listingId
 * @param {Object[]} [history]
 * @returns {boolean}
 */
export const wasViewed = (listingId, history = getHistory()) =>
  history.some(l => l.listingId === listingId);

/**
 * Sort history by most recent (already sorted by addToHistory).
 * @param {Object[]} history
 * @returns {Object[]}
 */
export const sortByRecent = (history) =>
  [...history].sort((a, b) => new Date(b.viewedAt) - new Date(a.viewedAt));
